/**
 * The draft's content (plan docs/plans/lms-plan.md 8.1 "a section list, a
 * slide list per section with layout pickers and the markdown subset in
 * textareas, a live preview pane that uses the same player components";
 * 6.1, 6.2, Appendix E, 18.11, 18.14; WP-44; R10, R12, R13).
 *
 * Left: "Course" (objectives, scope, disclaimer, takeaways, glossary, the
 * competency checklist) and the sections, each with its lint count. Right:
 * the chosen part's form. A section's slides are edited one at a time beside
 * the player's own SlideView; its reading is a markdown textarea beside
 * MdLite's rendering (18.11: "MdLite renders the reading preview").
 *
 * Course builder (docs/plans/lms-course-builder-plan.md 8.4, WP-B31): a
 * section-actions slot (the builder's Regenerate and Apply an instruction
 * buttons, rendered by the parent only when the builder is on), a `locked`
 * flag that disables the chosen part's form while a section run rewrites the
 * draft (the part list stays live so the admin can read), and `onSelect`,
 * which tells the parent which section is chosen (the Sources panel follows it).
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { outlineBtnCls } from '../../../../ui/styles';
import type { ChecklistItem, CourseCategory, CourseContent, CourseKind, CourseSection, GlossaryEntry, Slide, SlideLayout } from '../../../content/types';
import { MdLite } from '../../../content/MdLite';
import type { LintError } from '../../../api/catalog';
import { SlideView } from '../../../components/player/SlideView';
import type { DeckSlide } from '../../../components/player/deck';
import type { SlideAnswer } from '../../../hooks/usePlayer';
import { NumberInput, RowActions, StringList, TextArea, TextInput } from './fields';
import {
  blankSection,
  blankSlide,
  lintSectionIndex,
  lintSlideIndex,
  MAX_OBJECTIVES,
  moveItem,
  nextSectionId,
  nextSlideId,
  profileFor,
  removeAt,
  replaceAt,
  SCENARIO_PROFILE,
} from './model';
import { SlideList, SlideForm } from './SlideForm';
import { cardCls, countBadgeCls, hintCls, labelCls, listRowCls, microLabelCls, subheadingCls } from './styles';

export interface ContentEditorProps {
  content: CourseContent;
  onChange: (content: CourseContent) => void;
  category: CourseCategory;
  kind: CourseKind;
  errors: LintError[];
  /** Builder plan 8.4: a section run is rewriting the draft; the form is read-only. */
  locked?: boolean;
  /** The chosen part: a section id, or null for the course-wide fields. */
  onSelect?: (sectionId: string | null) => void;
  /** Builder plan 8.4: the section-actions slot, shown at the top of a section's form. */
  sectionActions?: (section: CourseSection, index: number) => ReactNode;
}

/** 'course' for the course-wide fields, else a section index. */
type Selection = 'course' | number;

function isContentError(e: LintError): boolean {
  return /\$?\.?content\b/.test(e.path || '') && !/questions\[/.test(e.path || '');
}

// ---------------------------------------------------------------------------
// The course-wide fields
// ---------------------------------------------------------------------------

function GlossaryEditor({ items, onChange }: { items: GlossaryEntry[]; onChange: (items: GlossaryEntry[]) => void }) {
  return (
    <fieldset className="space-y-2" data-testid="lms-glossary-editor">
      <legend className={labelCls}>Glossary (every abbreviation used twice needs an entry, 6.2 rule 8)</legend>
      {items.map((g, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2">
          <TextInput className="w-40" label={`Term ${i + 1}`} value={g.term} onChange={(term) => onChange(replaceAt(items, i, { ...g, term }))} />
          <TextInput
            className="min-w-[12rem] flex-1"
            label={`Definition ${i + 1}`}
            value={g.definition}
            onChange={(definition) => onChange(replaceAt(items, i, { ...g, definition }))}
          />
          <RowActions index={i} count={items.length} label={`term ${i + 1}`} onMove={(to) => onChange(moveItem(items, i, to))} onRemove={() => onChange(removeAt(items, i))} />
        </div>
      ))}
      <button type="button" className={outlineBtnCls()} onClick={() => onChange([...items, { term: '', definition: '' }])}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        Add term
      </button>
    </fieldset>
  );
}

function ChecklistEditor({ items, onChange }: { items: ChecklistItem[]; onChange: (items: ChecklistItem[]) => void }) {
  const nextId = () => {
    const used = new Set(items.map((c) => c.id));
    let n = items.length + 1;
    while (used.has(`c${n}`)) n += 1;
    return `c${n}`;
  };
  return (
    <fieldset className="space-y-2" data-testid="lms-checklist-editor">
      <legend className={labelCls}>Competency checklist (8 to 20 observable steps an observer marks met or not met, 6.1, R18)</legend>
      {items.map((c, i) => (
        <div key={c.id} className="flex items-end gap-2">
          <span className={`${microLabelCls} w-8 pb-2`}>{c.id}</span>
          <TextInput className="flex-1" label={`Step ${c.id}`} value={c.text} onChange={(text) => onChange(replaceAt(items, i, { ...c, text }))} />
          <RowActions index={i} count={items.length} label={`step ${c.id}`} onMove={(to) => onChange(moveItem(items, i, to))} onRemove={() => onChange(removeAt(items, i))} />
        </div>
      ))}
      <button
        type="button"
        className={`${outlineBtnCls()} disabled:cursor-not-allowed disabled:opacity-50`}
        onClick={() => onChange([...items, { id: nextId(), text: '' }])}
        disabled={items.length >= 20}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Add step
      </button>
    </fieldset>
  );
}

function CourseWideForm({ content, onChange, category, kind }: Pick<ContentEditorProps, 'content' | 'onChange' | 'category' | 'kind'>) {
  const scenario = profileFor(category) === 'scenario';
  const maxObjectives = scenario ? SCENARIO_PROFILE.objectives[1] : MAX_OBJECTIVES;
  const set = (patch: Partial<CourseContent>) => onChange({ ...content, ...patch });
  const relatedCourse = (content as CourseContent & { related_course?: string | null }).related_course ?? '';
  return (
    <div className="space-y-4" data-testid="lms-course-wide-form">
      <StringList
        label="Learning objectives (start each with a measurable verb: identify, explain, apply, decide, perform, list, recognize)"
        items={content.learning_objectives || []}
        onChange={(learning_objectives) => set({ learning_objectives })}
        max={maxObjectives}
        addLabel="Add objective"
        itemName="Objective"
        testId="lms-objectives"
        hint={scenario ? '1 to 3 for a scenario (6.5).' : `At most ${MAX_OBJECTIVES} (18.14); every one is tested by a question (6.2 rule 1).`}
      />
      <TextArea
        label="What this course does not cover (6.2 rule 11)"
        value={content.not_covered || ''}
        onChange={(not_covered) => set({ not_covered })}
        rows={2}
        testId="lms-not-covered"
      />
      {category === 'clinical_skills' ? (
        <TextArea
          label="Disclaimer (clinical skills, 6.3: on the first slide and the first reading section)"
          value={content.disclaimer || ''}
          onChange={(disclaimer) => set({ disclaimer: disclaimer || null })}
          rows={2}
        />
      ) : null}
      <StringList
        label="Key takeaways (the last slide and the last reading section)"
        items={content.key_takeaways || []}
        onChange={(key_takeaways) => set({ key_takeaways })}
        max={scenario ? SCENARIO_PROFILE.takeaways[1] : 8}
        addLabel="Add takeaway"
        itemName="Takeaway"
        testId="lms-takeaways"
        hint={scenario ? '2 to 4 for a scenario.' : '5 to 8 (6.1).'}
      />
      <GlossaryEditor items={content.glossary || []} onChange={(glossary) => set({ glossary })} />
      {kind === 'competency' ? (
        <ChecklistEditor items={content.competency_checklist || []} onChange={(competency_checklist) => set({ competency_checklist })} />
      ) : null}
      {scenario ? (
        <TextInput
          label="Related course (the builtin slug this scenario refreshes; optional)"
          value={relatedCourse}
          onChange={(v) => onChange({ ...content, related_course: v || null } as CourseContent)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One section
// ---------------------------------------------------------------------------

function SlidePreview({ slide }: { slide: Slide }) {
  const [answer, setAnswer] = useState<SlideAnswer | undefined>(undefined);
  const entry: DeckSlide = { key: slide.id, slide, synthetic: null, sectionIndex: 0, persisted: false, blocking: false };
  return (
    <div className={`${cardCls} space-y-2`} data-testid="lms-slide-preview">
      <div className={microLabelCls}>Preview (as the learner sees it)</div>
      <SlideView
        entry={entry}
        answer={answer}
        onSelect={(optionId, multi) =>
          setAnswer((a) => {
            const selected = a?.selected ?? [];
            if (a?.checked) return a;
            if (multi) {
              return { selected: selected.includes(optionId) ? selected.filter((x) => x !== optionId) : [...selected, optionId], checked: false };
            }
            return { selected: [optionId], checked: false };
          })
        }
        onCheck={() => setAnswer((a) => (a ? { ...a, checked: true } : a))}
        onChoose={(optionId) => setAnswer({ selected: [optionId], checked: true })}
      />
      {answer ? (
        <button type="button" className={outlineBtnCls()} onClick={() => setAnswer(undefined)}>
          Reset the preview answer
        </button>
      ) : null}
    </div>
  );
}

function SectionForm({
  section,
  index,
  count,
  content,
  onChange,
  onMove,
  onRemove,
  errors,
  actions,
}: {
  section: CourseSection;
  index: number;
  count: number;
  content: CourseContent;
  onChange: (section: CourseSection) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
  errors: LintError[];
  actions?: ReactNode;
}) {
  const [slideIndex, setSlideIndex] = useState(0);
  const slides = Array.isArray(section.slides) ? section.slides : [];
  const current = slides[Math.min(slideIndex, slides.length - 1)] ?? null;
  const currentIndex = current ? slides.indexOf(current) : -1;
  const set = (patch: Partial<CourseSection>) => onChange({ ...section, ...patch });
  const slideErrors = (i: number) => errors.filter((e) => lintSlideIndex(e.path) === i).length;

  const addSlide = (layout: SlideLayout) => {
    const id = nextSlideId(content, section.id);
    set({ slides: [...slides, blankSlide(layout, id)] });
    setSlideIndex(slides.length);
  };

  return (
    <div className="space-y-4" data-testid="lms-section-form" data-section-id={section.id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className={microLabelCls}>{`Section ${index + 1} (id ${section.id})`}</div>
        <RowActions index={index} count={count} label={`section ${index + 1}`} onMove={onMove} onRemove={onRemove} canRemove={count > 1} />
      </div>
      {actions ? <div data-testid="lms-section-actions-slot">{actions}</div> : null}
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_10rem]">
        <TextInput label="Section title" value={section.title} onChange={(title) => set({ title })} testId="lms-section-title" />
        <NumberInput
          label="Minutes"
          value={typeof section.estimated_minutes === 'number' ? section.estimated_minutes : null}
          onChange={(v) => v !== null && set({ estimated_minutes: v })}
          min={1}
          max={120}
        />
      </div>
      <TextInput
        label="Why this matters (one sentence; the section opens with it, 6.2 rule 9)"
        value={section.why_it_matters || ''}
        onChange={(why_it_matters) => set({ why_it_matters })}
      />

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <SlideList
            slides={slides}
            selected={currentIndex}
            onSelect={setSlideIndex}
            onChange={(next) => set({ slides: next })}
            onAdd={addSlide}
            errorCount={slideErrors}
          />
          {current ? (
            <SlideForm key={current.id} slide={current} onChange={(slide) => set({ slides: replaceAt(slides, currentIndex, slide) })} />
          ) : (
            <p className={hintCls}>This section has no slides yet. A section holds 3 to 9 (6.1), with a knowledge check among them.</p>
          )}
        </div>
        <div className="space-y-4">{current ? <SlidePreview key={current.id} slide={current} /> : null}</div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <TextArea
          label="Reading (the full document for this section, in the markdown subset)"
          value={section.reading_md || ''}
          onChange={(reading_md) => set({ reading_md })}
          rows={12}
          markdown
          testId="lms-section-reading"
          hint="Paragraphs separated by blank lines; ### headings; **bold**, *italic*; - and 1. lists; [links](https://...); tables; > quotations with attribution (Appendix E)."
        />
        <div className={`${cardCls} space-y-2`} data-testid="lms-reading-preview">
          <div className={microLabelCls}>Reading preview</div>
          <MdLite source={section.reading_md || ''} size="sm" />
        </div>
      </div>

      <StringList
        label="What to remember (2 to 4 bullets that close the section, 6.2 rule 9)"
        items={section.what_to_remember || []}
        onChange={(what_to_remember) => set({ what_to_remember })}
        max={4}
        addLabel="Add line"
        itemName="Line"
        testId="lms-what-to-remember"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

export function ContentEditor({ content, onChange, category, kind, errors, locked = false, onSelect, sectionActions }: ContentEditorProps) {
  const sections = Array.isArray(content.sections) ? content.sections : [];
  const [selection, setSelection] = useState<Selection>(sections.length > 0 ? 0 : 'course');
  const selectedIndex = typeof selection === 'number' ? Math.min(selection, sections.length - 1) : -1;
  const section = selectedIndex >= 0 ? sections[selectedIndex] : null;
  const contentErrors = errors.filter(isContentError);
  const sectionErrors = (i: number) => contentErrors.filter((e) => lintSectionIndex(e.path) === i);
  const courseWideErrors = contentErrors.filter((e) => lintSectionIndex(e.path) === null).length;
  const setSections = (next: CourseSection[]) => onChange({ ...content, sections: next });
  const selectedId = section ? section.id : null;

  useEffect(() => {
    if (onSelect) onSelect(selectedId);
    // onSelect is the parent's setter; only the chosen id matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const addSection = () => {
    const id = nextSectionId(content);
    setSections([...sections, blankSection(id)]);
    setSelection(sections.length);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[15rem_minmax(0,1fr)]" data-testid="lms-content-editor">
      <nav className="space-y-3" aria-label="Course parts">
        <button
          type="button"
          className={listRowCls(selection === 'course')}
          onClick={() => setSelection('course')}
          aria-current={selection === 'course' ? 'true' : undefined}
          data-testid="lms-part-course"
        >
          <span className="min-w-0 flex-1 truncate">Course: objectives, takeaways, glossary</span>
          {courseWideErrors > 0 ? <span className={countBadgeCls}>{courseWideErrors}</span> : null}
        </button>
        <div className="space-y-1">
          <div className={`${microLabelCls} px-2.5`}>{`Sections (${sections.length})`}</div>
          <ol className="space-y-1">
            {sections.map((s, i) => {
              const count = sectionErrors(i).length;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    className={listRowCls(selectedIndex === i)}
                    onClick={() => setSelection(i)}
                    aria-current={selectedIndex === i ? 'true' : undefined}
                    data-testid={`lms-part-section-${i}`}
                  >
                    <span className="w-5 flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">{i + 1}</span>
                    <span className="min-w-0 flex-1 truncate">{s.title || `Untitled section ${s.id}`}</span>
                    {count > 0 ? <span className={countBadgeCls}>{count}</span> : null}
                  </button>
                </li>
              );
            })}
          </ol>
          <button
            type="button"
            className={`${outlineBtnCls()} w-full justify-center disabled:cursor-not-allowed disabled:opacity-50`}
            onClick={addSection}
            disabled={locked || (profileFor(category) === 'scenario' && sections.length >= 1)}
            data-testid="lms-add-section"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add section
          </button>
        </div>
      </nav>

      <fieldset className="min-w-0" disabled={locked} data-testid="lms-content-form" data-locked={locked ? 'true' : 'false'}>
        {section ? (
          <SectionForm
            key={section.id}
            section={section}
            index={selectedIndex}
            count={sections.length}
            content={content}
            onChange={(next) => setSections(replaceAt(sections, selectedIndex, next))}
            onMove={(to) => {
              if (to < 0 || to >= sections.length) return;
              setSections(moveItem(sections, selectedIndex, to));
              setSelection(to);
            }}
            onRemove={() => {
              setSections(removeAt(sections, selectedIndex));
              setSelection(sections.length > 1 ? Math.max(0, selectedIndex - 1) : 'course');
            }}
            errors={sectionErrors(selectedIndex)}
            actions={sectionActions ? sectionActions(section, selectedIndex) : null}
          />
        ) : (
          <div className="space-y-2">
            <h3 className={subheadingCls}>The course as a whole</h3>
            <CourseWideForm content={content} onChange={onChange} category={category} kind={kind} />
          </div>
        )}
      </fieldset>
    </div>
  );
}

export default ContentEditor;
